import React, { useMemo } from 'react';
import { components, ValueContainerProps } from 'react-select';
import { ISelectOption } from '../../base';
import { CoderRuleSummary } from '../../../services/worker-configuration/automation/worker-automation.types';
import { normalizeDisabledCodingRules } from '../../../services/worker-configuration/automation/worker-automation.utils';
import * as S from './configuration-rule.styles';

interface IConfigurationCoderRulesSelectProps {
    coderRules: CoderRuleSummary[];
    disableCodingRules: unknown;
    disabled?: boolean;
    /** Set when the last fetch failed — kept distinct from an account genuinely having zero coder
     *  rules (PR review, 2026-09-28): both used to render as the same empty state. */
    error?: string | null;
    loading?: boolean;
    onChange: (ruleNumbers: number[]) => void;
}

function formatCoderRuleOptionLabel(rule: CoderRuleSummary): React.ReactElement {
    return (
        <>
            <S.CoderRuleOptionNumber>#{rule.ruleNumber}</S.CoderRuleOptionNumber>
            <S.CoderRuleOptionLabel inactive={!rule.isActive}>
                {rule.description.trim() || 'Untitled rule'}
            </S.CoderRuleOptionLabel>
            {!rule.isActive && <S.CoderRuleOptionTag>inactive</S.CoderRuleOptionTag>}
        </>
    );
}

/**
 * With dozens of coder rules a chip per enabled rule floods the control, so the selected values
 * collapse to one summary line. The menu stays searchable; the input is kept so typing filters it.
 */
const CoderRulesValueContainer = ({ children, ...props }: ValueContainerProps<ISelectOption<number>, true>) => {
    const count = props.getValue().length;
    if (count === 0) {
        return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
    }
    const childArray = React.Children.toArray(children);
    const input = childArray[childArray.length - 1];
    const total = props.selectProps.options.length;

    return (
        <components.ValueContainer {...props}>
            {!props.selectProps.inputValue && (
                <S.CoderRulesValueSummary>
                    {count === total ? `All ${total} rules run` : `${count} of ${total} rules run`}
                </S.CoderRulesValueSummary>
            )}
            {input}
        </components.ValueContainer>
    );
};

const SELECT_COMPONENTS = { ValueContainer: CoderRulesValueContainer };

/**
 * Every coder rule runs by default (all "checked"); unchecking one disables it for claims this
 * action fires on. The underlying value is the DISABLED set (`getDefaultActionValue`), so a coder
 * rule created after this action was configured still runs — nothing here has to know about it.
 */
export const ConfigurationCoderRulesSelect: React.FC<IConfigurationCoderRulesSelectProps> = ({
    coderRules,
    disableCodingRules,
    disabled,
    error,
    loading,
    onChange,
}) => {
    const disabledSet = useMemo(
        () => new Set(normalizeDisabledCodingRules(disableCodingRules)),
        [disableCodingRules],
    );

    // A saved rule number the current catalog no longer has is kept in the list rather than
    // silently dropped from the stored value — it still shows, toggleable, as "#N (not found)".
    const knownNumbers = new Set(coderRules.map(rule => rule.ruleNumber));
    const missingNumbers = [...disabledSet].filter(ruleNumber => !knownNumbers.has(ruleNumber));

    const allOptions: ISelectOption<number>[] = useMemo(
        () => [
            ...coderRules.map(rule => ({
                value: rule.ruleNumber,
                label: formatCoderRuleOptionLabel(rule),
            })),
            ...missingNumbers.map(ruleNumber => ({
                value: ruleNumber,
                label: `#${ruleNumber} (not found)`,
            })),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [coderRules, missingNumbers.join(',')],
    );

    // Option labels are React elements, which react-select's default filter stringifies to
    // "[object Object]" — so without this only the number (the option value) was searchable.
    const searchTextByNumber = useMemo(() => {
        const map = new Map<number, string>();
        coderRules.forEach(rule => map.set(rule.ruleNumber, `#${rule.ruleNumber} ${rule.description}`.toLowerCase()));
        return map;
    }, [coderRules]);

    const filterOption = (option: { value: unknown }, input: string): boolean => {
        const query = input.trim().toLowerCase();
        if (!query) {
            return true;
        }
        const text = searchTextByNumber.get(option.value as number) ?? `#${option.value} (not found)`;
        return query.split(/\s+/).every(word => text.includes(word));
    };

    const selectedOptions = allOptions.filter(
        option => option.value !== undefined && !disabledSet.has(option.value),
    );

    const totalCount = allOptions.length;
    const enabledCount = selectedOptions.length;

    return (
        <div>
            <S.FieldSelect
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={SELECT_COMPONENTS}
                filterOption={filterOption}
                options={allOptions}
                value={selectedOptions}
                placeholder={loading ? 'Loading coder rules…' : 'Search coder rules…'}
                disabled={disabled || loading}
                onChange={selected => {
                    const options = Array.isArray(selected) ? selected : selected ? [selected] : [];
                    const enabledNumbers = new Set(
                        options.map(option => option.value).filter((value): value is number => value !== undefined),
                    );
                    const allNumbers = allOptions
                        .map(option => option.value)
                        .filter((value): value is number => value !== undefined);

                    onChange(allNumbers.filter(ruleNumber => !enabledNumbers.has(ruleNumber)));
                }}
            />
            <S.CoderRulesSummary>
                {loading
                    ? 'Loading coder rules…'
                    : error
                      ? // Whatever was already saved on this rule is unaffected by this fetch
                        // failing — it just cannot be shown with names/descriptions right now, so
                        // this deliberately does not claim anything about what is or isn't running.
                        `${error} Try again, or reload the page.`
                      : totalCount === 0
                        ? 'No coder rules on this account.'
                        : enabledCount === totalCount
                          ? `All ${totalCount} coder rule${totalCount === 1 ? '' : 's'} run.`
                          : `${totalCount - enabledCount} of ${totalCount} coder rule${totalCount === 1 ? '' : 's'} skipped.`}
            </S.CoderRulesSummary>
        </div>
    );
};
